import React from 'react';
import { makeStyles, Theme, createStyles } from '@material-ui/core';
import { ValidationRule, TerraformType } from '../types';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: theme.spacing(1),
      marginTop: theme.spacing(0.5),
    },
    hint: {
      fontSize: '0.7rem',
      padding: '2px 6px',
      borderRadius: 4,
      backgroundColor:
        theme.palette.type === 'dark'
          ? 'rgba(255, 255, 255, 0.08)'
          : 'rgba(0, 0, 0, 0.06)',
      color: theme.palette.text.secondary,
    },
  }),
);

interface ValidationHintsProps {
  validation?: ValidationRule[];
  type: TerraformType;
}

/**
 * Component to display validation constraints as helper text
 * Shows pattern, length, range, and enum constraints
 */
export const ValidationHints: React.FC<ValidationHintsProps> = ({
  validation,
  type,
}) => {
  const classes = useStyles();

  if (!validation || validation.length === 0) {
    return null;
  }

  const hints: string[] = [];

  validation.forEach(rule => {
    // Pattern constraint
    if (rule.pattern) {
      hints.push(`Pattern: ${rule.pattern}`);
    }

    // Enum constraint
    if (rule.enum && rule.enum.length > 0) {
      const enumDisplay =
        rule.enum.length <= 3
          ? rule.enum.join(', ')
          : `${rule.enum.slice(0, 3).join(', ')}, +${rule.enum.length - 3} more`;
      hints.push(`Options: ${enumDisplay}`);
    }

    // Length constraints (for strings)
    if (type === 'string') {
      if (rule.minLength !== undefined && rule.maxLength !== undefined) {
        hints.push(`Length: ${rule.minLength}-${rule.maxLength}`);
      } else if (rule.minLength !== undefined) {
        hints.push(`Min length: ${rule.minLength}`);
      } else if (rule.maxLength !== undefined) {
        hints.push(`Max length: ${rule.maxLength}`);
      }
    }

    // Range constraints (for numbers)
    if (type === 'number') {
      if (rule.min !== undefined && rule.max !== undefined) {
        hints.push(`Range: ${rule.min}-${rule.max}`);
      } else if (rule.min !== undefined) {
        hints.push(`Min: ${rule.min}`);
      } else if (rule.max !== undefined) {
        hints.push(`Max: ${rule.max}`);
      }
    }
  });

  // Remove duplicates
  const uniqueHints = [...new Set(hints)];

  if (uniqueHints.length === 0) {
    return null;
  }

  return (
    <div className={classes.container}>
      {uniqueHints.map((hint, index) => (
        <span key={index} className={classes.hint}>
          {hint}
        </span>
      ))}
    </div>
  );
};
